import { Dialog } from '@angular/cdk/dialog';
import { Component, inject, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { take } from 'rxjs';
import { DeviceDto } from 'src/app/models/device.model';
import { LicenseBatchDto } from 'src/app/models/license.model';
import { UserRole } from 'src/app/models/user.model';
import { AlertService } from 'src/app/services/alert.service';
import { LicenseBatchStore } from 'src/app/services/license-batch.store';
import { selectProfileState } from 'src/app/stores/profile/profile.selector';
import {
  ColumnDefinition,
  DataColumnDefinition,
  PaginatorConfig,
} from '../qs-table/column-definition.interface';
import { SoftwareOverridesSaved } from '../device-license-manage-modal/device-software-override/device-software-override.component';
import {
  LicenseBatchSoftwareOverrideModalComponent,
  LicenseBatchSoftwareOverrideModalData,
} from './license-batch-software-override-modal/license-batch-software-override-modal.component';

type LicenseBatchDeviceRow = {
  no: number;
  id: string;
  alias: string;
  deviceType: string;
  model: string;
  unitId: string;
  company: string;
};

type LicenseBatchUserRow = {
  no: number;
  id: string;
  name: string;
  email: string;
};

@Component({
  selector: 'app-license-batch-details',
  templateUrl: './license-batch-details.component.html',
  styleUrls: ['./license-batch-details.component.scss'],
  standalone: false,
})
export class LicenseBatchDetailsComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly alertService = inject(AlertService);
  private readonly dialog = inject(Dialog);
  protected readonly licenseBatchStore = inject(LicenseBatchStore);
  private readonly profile = toSignal(this.store.select(selectProfileState), {
    requireSync: false,
  });

  batch: LicenseBatchDto | null = null;
  linkedDevicesRows: LicenseBatchDeviceRow[] = [];
  linkedUsersRows: LicenseBatchUserRow[] = [];
  loading = false;

  readonly isSearchEnabled = true;
  readonly isPaginatorEnabled = true;
  readonly paginatorConfig: PaginatorConfig = {
    pageSize: 15,
    pageSizeOptions: [5, 10, 15, 25],
  };

  readonly columns: ColumnDefinition<LicenseBatchDeviceRow>[] = [
    {
      type: 'data',
      field: 'no',
      header: 'No',
      sortFn: (a, b, multiplier) => (a.no - b.no) * multiplier,
    } as DataColumnDefinition<LicenseBatchDeviceRow>,
    {
      type: 'data',
      field: 'id',
      header: 'Device ID',
      sortFn: (a, b, multiplier) => a.id.localeCompare(b.id) * multiplier,
    } as DataColumnDefinition<LicenseBatchDeviceRow>,
    {
      type: 'data',
      field: 'alias',
      header: 'Alias',
      sortFn: (a, b, multiplier) => a.alias.localeCompare(b.alias) * multiplier,
    } as DataColumnDefinition<LicenseBatchDeviceRow>,
    {
      type: 'data',
      field: 'deviceType',
      header: 'Type',
      sortFn: (a, b, multiplier) =>
        a.deviceType.localeCompare(b.deviceType) * multiplier,
    } as DataColumnDefinition<LicenseBatchDeviceRow>,
    {
      type: 'data',
      field: 'model',
      header: 'Model',
      sortFn: (a, b, multiplier) => a.model.localeCompare(b.model) * multiplier,
    } as DataColumnDefinition<LicenseBatchDeviceRow>,
    {
      type: 'data',
      field: 'unitId',
      header: 'Unit ID',
      sortFn: (a, b, multiplier) =>
        a.unitId.localeCompare(b.unitId) * multiplier,
    } as DataColumnDefinition<LicenseBatchDeviceRow>,
    {
      type: 'data',
      field: 'company',
      header: 'Company',
      sortFn: (a, b, multiplier) =>
        a.company.localeCompare(b.company) * multiplier,
    } as DataColumnDefinition<LicenseBatchDeviceRow>,
  ];

  readonly userColumns: ColumnDefinition<LicenseBatchUserRow>[] = [
    {
      type: 'data',
      field: 'no',
      header: 'No',
    } as DataColumnDefinition<LicenseBatchUserRow>,
    {
      type: 'data',
      field: 'name',
      header: 'Name',
    } as DataColumnDefinition<LicenseBatchUserRow>,
    {
      type: 'data',
      field: 'email',
      header: 'Email',
    } as DataColumnDefinition<LicenseBatchUserRow>,
    {
      type: 'data',
      field: 'id',
      header: 'User ID',
    } as DataColumnDefinition<LicenseBatchUserRow>,
  ];

  ngOnInit(): void {
    const batchId = this.route.snapshot.paramMap.get('id');
    if (!batchId) {
      this.alertService.error('Missing license batch id.');
      this.navigateBack();
      return;
    }

    this.loading = true;
    this.licenseBatchStore
      .getBatchFromStoreOrFetch({
        batchId,
        projection: 'FULL',
        forceRefresh: true,
      })
      .pipe(take(1))
      .subscribe({
        next: (batch) => {
          this.batch = batch;
          this.linkedDevicesRows = this.mapLinkedDevicesRows(batch);
          this.linkedUsersRows = this.mapLinkedUsersRows(batch);
          this.loading = false;
        },
        error: (error: unknown) => {
          this.loading = false;
          this.alertService.error(this.getErrorMessage(error));
          this.navigateBack();
        },
      });
  }

  get isMasterAdmin(): boolean {
    return this.profile()?.isMasteradmin ?? false;
  }

  get canManageLicenseAssignments(): boolean {
    const profile = this.profile();
    return profile?.isMasteradmin === true || profile?.role === UserRole.ADMIN;
  }

  private get isAdminRoute(): boolean {
    return this.router.url.includes('/admin/');
  }

  get linkedDevicesCount(): number {
    return this.linkedDevicesRows.length;
  }

  get isUserTarget(): boolean {
    return this.batch?.product?.target === 'USER';
  }

  get linkedUsersCount(): number {
    return this.linkedUsersRows.length;
  }

  get detailsTitle(): string {
    return this.batch?.name?.trim() || 'License Batch Details';
  }

  get batchId(): string {
    return this.batch?.id ?? '-';
  }

  get companyLabel(): string {
    return this.batch?.company?.name || this.batch?.companyId || 'Not assigned';
  }

  get productLabel(): string {
    const name = this.batch?.product?.name?.trim();
    const id = this.batch?.licensedProductId?.trim();
    if (name) {
      return name;
    }
    if (id) {
      return id;
    }
    return 'Not assigned';
  }

  get scopeLabel(): string {
    // A scope whose tag is gone from Spadok comes without a name
    return (
      this.batch?.scope?.name?.trim() ||
      this.batch?.scope?.id?.trim() ||
      'Not assigned'
    );
  }

  get canOverrideSoftware(): boolean {
    return this.isMasterAdmin && !this.isUserTarget;
  }

  get softwareOverrideLabels(): string[] {
    const overrides = this.batch?.softwareVersionOverrides;
    const unresolvedIds = new Set(overrides?.unresolvedOverrideIds ?? []);
    const bundleLabels = (overrides?.bundleOverrides ?? []).map((bundle) =>
      unresolvedIds.has(bundle.id)
        ? `Bundle ${bundle.id}: no longer in Spadok`
        : bundle.releaseName
          ? `${bundle.releaseName}: ${bundle.name}`
          : `Bundle: ${bundle.name}`,
    );
    const artifactLabels = (overrides?.artifactOverrides ?? []).map(
      (artifact) =>
        `${artifact.projectName || artifact.projectId} ${
          unresolvedIds.has(artifact.id)
            ? 'artifact no longer in Spadok'
            : artifact.version
        }`,
    );
    return [...bundleLabels, ...artifactLabels];
  }

  openSoftwareOverride(): void {
    const batch = this.batch;
    if (!batch || !this.canOverrideSoftware) {
      return;
    }

    this.dialog
      .open<SoftwareOverridesSaved, LicenseBatchSoftwareOverrideModalData>(
        LicenseBatchSoftwareOverrideModalComponent,
        { data: { batch } },
      )
      .closed.pipe(take(1))
      .subscribe((saved) => {
        if (saved && this.batch) {
          this.batch = {
            ...this.batch,
            softwareVersionOverrides: {
              artifactOverrides: saved.artifactOverrides,
              bundleOverrides: saved.bundleOverrides,
              unresolvedOverrideIds: saved.unresolvedOverrideIds,
            },
            licenseRedemptionInfo: {
              ...this.batch.licenseRedemptionInfo,
              components: saved.softwareSelectionResolutions,
            },
          };
        }
      });
  }

  get componentNames(): string[] {
    const rawComponents =
      (this.batch?.product as { licensedComponents?: unknown[] } | undefined)
        ?.licensedComponents ?? [];
    return rawComponents
      .map((component) => {
        if (typeof component === 'string') {
          return component.trim();
        }
        if (component && typeof component === 'object') {
          const typedComponent = component as {
            componentName?: string;
            id?: string;
          };
          return (
            typedComponent.componentName?.trim() ||
            typedComponent.id?.trim() ||
            ''
          );
        }
        return '';
      })
      .filter((name) => !!name);
  }

  navigateToManageBatch(): void {
    const batchId = this.batch?.id;
    if (!batchId || !this.isMasterAdmin) {
      return;
    }

    this.router.navigate(['/admin/licenses/batches', batchId, 'edit']);
  }

  navigateToManageDevices(): void {
    const batchId = this.batch?.id;
    if (!batchId) {
      return;
    }

    this.router.navigate([
      this.isAdminRoute ? '/admin/licenses/batches' : '/licenses/batches',
      batchId,
      'devices',
    ]);
  }

  navigateToManageUsers(): void {
    const batchId = this.batch?.id;
    if (!batchId) return;
    this.router.navigate([
      this.isAdminRoute ? '/admin/licenses/batches' : '/licenses/batches',
      batchId,
      'users',
    ]);
  }

  navigateBack(): void {
    this.router.navigate([
      this.isAdminRoute ? '/admin/licenses/batches' : '/licenses/batches',
    ]);
  }

  formatDate(dateString?: string): string {
    if (!dateString) {
      return 'Not set';
    }

    const parsedDate = new Date(dateString);
    if (isNaN(parsedDate.getTime())) {
      return dateString;
    }

    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(parsedDate);
  }

  private toDeviceRow(
    deviceId: string,
    device: DeviceDto | undefined,
    index: number,
  ): LicenseBatchDeviceRow {
    const details = (device?.details ?? null) as {
      type?: string;
      aircraftModel?: {
        name?: string;
        hardwareRevisionString?: string;
      };
      unitId?: number;
    } | null;

    const model =
      device?.deviceSummary?.model?.trim() ||
      device?.deviceSummary?.modelName?.trim() ||
      details?.aircraftModel?.name?.trim() ||
      details?.aircraftModel?.hardwareRevisionString?.trim() ||
      '-';

    return {
      no: index + 1,
      id: deviceId || '-',
      alias:
        device?.nameAlias?.trim() ||
        device?.deviceSummary?.defaultName?.trim() ||
        '-',
      deviceType: device?.deviceType?.trim() || details?.type?.trim() || '-',
      model,
      unitId:
        device?.deviceSummary?.unitId?.trim() ||
        (details?.unitId !== undefined ? String(details.unitId) : '-'),
      company: device?.company?.name?.trim() || '-',
    };
  }

  private mapLinkedDevicesRows(
    batch: LicenseBatchDto,
  ): LicenseBatchDeviceRow[] {
    const licensedDevices = batch.licensedDevices ?? [];
    const deviceById = new Map<string, DeviceDto>();

    licensedDevices.forEach((device) => {
      const normalizedId = device.deviceId?.trim();
      if (normalizedId) {
        deviceById.set(normalizedId, device);
      }
    });

    const orderedIds: string[] = [];
    const seenIds = new Set<string>();
    (batch.licensedDeviceIds ?? []).forEach((deviceId) => {
      const normalizedId = deviceId?.trim();
      if (!normalizedId || seenIds.has(normalizedId)) {
        return;
      }
      seenIds.add(normalizedId);
      orderedIds.push(normalizedId);
    });

    if (orderedIds.length === 0) {
      deviceById.forEach((_, deviceId) => {
        if (!seenIds.has(deviceId)) {
          seenIds.add(deviceId);
          orderedIds.push(deviceId);
        }
      });
    } else {
      deviceById.forEach((_, deviceId) => {
        if (!seenIds.has(deviceId)) {
          seenIds.add(deviceId);
          orderedIds.push(deviceId);
        }
      });
    }

    return orderedIds.map((deviceId, index) =>
      this.toDeviceRow(deviceId, deviceById.get(deviceId), index),
    );
  }

  private mapLinkedUsersRows(batch: LicenseBatchDto): LicenseBatchUserRow[] {
    const usersById = new Map(
      (batch.licensedUsers ?? []).map((user) => [user.id, user]),
    );
    const userIds = new Set(batch.licensedUserIds ?? []);
    usersById.forEach((_, id) => userIds.add(id));
    return [...userIds].filter(Boolean).map((id, index) => {
      const user = usersById.get(id);
      return {
        no: index + 1,
        id,
        name:
          [user?.firstName, user?.lastName].filter(Boolean).join(' ') || '-',
        email: user?.email?.trim() || '-',
      };
    });
  }

  private getErrorMessage(error: unknown): string {
    if (!error) {
      return 'Failed to load license batch details.';
    }
    if (typeof error === 'string') {
      return error;
    }
    if (typeof error === 'object' && error !== null) {
      const typedError = error as {
        error?: { message?: string; title?: string };
        message?: string;
      };
      if (typedError.error?.message) {
        return typedError.error.message;
      }
      if (typedError.error?.title) {
        return typedError.error.title;
      }
      if (typedError.message) {
        return typedError.message;
      }
    }
    return 'Failed to load license batch details.';
  }
}
